"use client";

import { useAuth } from "@clerk/clerk-react";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { getLanguageFromPath, addLanguageToPath } from "@/lib/language";

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Route wrapper that requires authentication.
 * Shows loading spinner while checking auth state,
 * redirects to sign-in if unauthenticated.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Show loading state while Clerk loads
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-lavender flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-rose-dark border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated
  if (!isSignedIn) {
    // Get current language and create language-aware sign-in path
    const currentLanguage = getLanguageFromPath(location.pathname);
    const signInPath = addLanguageToPath("/sign-in", currentLanguage);

    // Save the current location to redirect back after sign-in
    return <Navigate to={signInPath} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
